import { useState, useEffect } from "react";
import { TbX, TbTag, TbToolsKitchen2, TbShield, TbShieldOff, TbShieldHalf } from "react-icons/tb";

const RatePlansDrawer = ({
  show,
  onHide,
  onCreate,
  onUpdate,
  hotelId,
  editingPlan
}) => {

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mealPlan, setMealPlan] = useState("none");
  const [policyType, setPolicyType] = useState("free");
  const [penaltyPercentage, setPenaltyPercentage] = useState(0);
  const [hoursBeforeArrival, setHoursBeforeArrival] = useState(24);

  const isEdit = Boolean(editingPlan);

  useEffect(() => {
    if (editingPlan) {
      setName(editingPlan.name || "");
      setDescription(editingPlan.description || "");
      setMealPlan(editingPlan.mealPlan || "none");
      setPolicyType(editingPlan.cancellationPolicy?.type || "free");
      setPenaltyPercentage(editingPlan.cancellationPolicy?.penaltyPercentage || 0);
      setHoursBeforeArrival(editingPlan.cancellationPolicy?.hoursBeforeArrival || 24);
    } else {
      resetForm();
    }
  }, [editingPlan, show]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setMealPlan("none");
    setPolicyType("free");
    setPenaltyPercentage(0);
    setHoursBeforeArrival(24);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const payload = {
      hotelId,
      name: name.trim(),
      description: description.trim(),
      mealPlan,
      cancellationPolicy: {
        type: policyType,
        hoursBeforeArrival: policyType !== "non_refundable" ? Number(hoursBeforeArrival) : 0,
        penaltyPercentage: policyType === "partial" ? Number(penaltyPercentage) : 0
      }
    };

    if (isEdit) {
      onUpdate(editingPlan._id, payload);
    } else {
      onCreate(payload);
    }

    resetForm();
    onHide();
  };

  if (!show) return null;

  const policyOptions = [
    {
      value: "free",
      label: "Annulation gratuite",
      desc: "Le client peut annuler sans frais",
      icon: <TbShield className="text-green-500 text-xl" />,
      color: "border-green-200 bg-green-50"
    },
    {
      value: "partial",
      label: "Annulation partielle",
      desc: "Des frais s'appliquent après un délai",
      icon: <TbShieldHalf className="text-yellow-500 text-xl" />,
      color: "border-yellow-200 bg-yellow-50"
    },
    {
      value: "non_refundable",
      label: "Non remboursable",
      desc: "Aucun remboursement en cas d'annulation",
      icon: <TbShieldOff className="text-red-500 text-xl" />,
      color: "border-red-200 bg-red-50"
    }
  ];

  const mealOptions = [
    { value: "none", label: "Aucun repas inclus", emoji: null },
    { value: "breakfast", label: "Petit-déjeuner", emoji: "🍳" },
    { value: "half_board", label: "Demi-pension", emoji: "🍽️" },
    { value: "full_board", label: "Pension complète", emoji: "🍱" }
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={() => { resetForm(); onHide(); }}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col">

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              {isEdit ? "Modifier le tarif" : "Créer un tarif"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isEdit ? "Modifiez les informations du tarif" : "Configurez un nouveau plan tarifaire"}
            </p>
          </div>
          <button
            onClick={() => { resetForm(); onHide(); }}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <TbX className="text-gray-500" />
          </button>
        </div>

        {/* CONTENT */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6">

          {/* Nom */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
              Nom du tarif *
            </label>
            <div className="relative">
              <TbTag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex : Tarif flexible, Non remboursable..."
                required
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
              Description <span className="text-gray-400 font-normal">(optionnel)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez ce tarif..."
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition resize-none"
            />
          </div>

          {/* Politique annulation */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
              Politique d'annulation *
            </label>
            <div className="flex flex-col gap-2">
              {policyOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => setPolicyType(opt.value)}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition
                    ${policyType === opt.value
                      ? opt.color + " border-opacity-100"
                      : "border-gray-100 hover:border-gray-200"
                    }
                  `}
                >
                  {opt.icon}
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-400">{opt.desc}</p>
                  </div>
                  <div className={`ml-auto w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    policyType === opt.value ? "border-[#3A9D23]" : "border-gray-300"
                  }`}>
                    {policyType === opt.value && (
                      <div className="w-2 h-2 rounded-full bg-[#3A9D23]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Heures avant annulation */}
          {policyType !== "non_refundable" && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                Annulation gratuite jusqu'à (heures avant arrivée)
              </label>
              <input
                type="number"
                min={0}
                value={hoursBeforeArrival}
                onChange={(e) => setHoursBeforeArrival(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
              />
              <p className="text-xs text-gray-400 mt-1">
                Ex: 24 = annulation gratuite jusqu'à 24h avant l'arrivée
              </p>
            </div>
          )}

          {/* Frais si partial */}
          {policyType === "partial" && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                Frais d'annulation (%)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={penaltyPercentage}
                onChange={(e) => setPenaltyPercentage(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#3A9D23] transition"
              />
            </div>
          )}

          {/* Repas */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
              Repas inclus
            </label>
            <div className="grid grid-cols-2 gap-2">
              {mealOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => setMealPlan(opt.value)}
                  className={`
                    flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition text-sm
                    ${mealPlan === opt.value
                      ? "border-[#3A9D23] bg-green-50 text-green-800 font-medium"
                      : "border-gray-100 text-gray-700 hover:border-gray-200"
                    }
                  `}
                >
                  {opt.emoji && <span>{opt.emoji}</span>}
                  <span>{opt.label}</span>
                </div>
              ))}
            </div>
          </div>

        </form>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            className={`
              w-full py-2.5 rounded-xl text-sm font-semibold transition
              ${!name.trim()
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#3A9D23] text-white hover:bg-[#33891E] active:scale-[0.98]"
              }
            `}
          >
            {isEdit ? "Mettre à jour" : "Créer le tarif"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default RatePlansDrawer;